import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    WhatsappShareButton,
    FacebookShareButton,
    FacebookMessengerShareButton,
    TelegramShareButton,
    TwitterShareButton,
    FacebookIcon,
    FacebookMessengerIcon,
    TelegramIcon,
    TwitterIcon,
    WhatsappIcon,
} from "react-share";
import { isMobile } from 'react-device-detect';

const BottomMenu = () => {
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [activeButton, setActiveButton] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false); // Toggle state

    const isInstalled = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;

    const navigate = useNavigate();
    const location = useLocation();
    const size = 45;
    const shareUrl = window.location.href;
    const isEnglish = location.pathname.includes('/en/');

    if (!isMobile) return null;

    // Logic to keep the toggle on the right for English and left for Arabic
    const toggleSideStyle = isEnglish ? { right: '15px' } : { left: '15px' };

    const handleShareClick = (platform) => {
        logShareEvent(platform);
        setIsShareOpen(false);
        setActiveButton(null);
    };

    const logShareEvent = async (platform) => {
        try {
            const isDev = window.location.hostname === "localhost";
            const collectionName = isDev ? "shareEventsDev" : "shareEvents";
            const { db } = await import('../firebaseConfig');
            const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
            await addDoc(collection(db, collectionName), {
                platform: platform,
                path: window.location.pathname,
                createdAt: serverTimestamp()
            });
        } catch (error) {
            console.error('Error logging share:', error);
        }
    };

    const handleHomeClick = () => {
        setActiveButton('home');
        setIsShareOpen(false);
        if (isEnglish) {
            navigate('/en/');
        } else {
            navigate('/');
        }
    };

    const handleFeedbackClick = () => {
        setActiveButton('feedback');
        setIsShareOpen(false);
        const feedbackBtn = document.getElementById('auto-feedback-btn');
        if (feedbackBtn) {
            feedbackBtn.click();
        }
    };

    const handleInstallClick = async () => {
        setActiveButton('install');
        setIsShareOpen(false);
        await logInstallClick();

        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;

        // Try Android/desktop install prompt
        if (window.__deferredInstallPrompt) {
            window.__deferredInstallPrompt.prompt();
            await window.__deferredInstallPrompt.userChoice;
            window.__deferredInstallPrompt = null;
        } else if (isIOS) {
            // Trigger the iOS modal in InstallAppBanner by clicking its button
            const installBtn = document.querySelector('[data-install-banner-btn]');
            if (installBtn) installBtn.click();
        }
    };


    const handleShareToggle = () => {
        setActiveButton(isShareOpen ? null : 'share');
        setIsShareOpen(!isShareOpen);
    };

    const logInstallClick = async () => {
        try {
            const { db } = await import('../firebaseConfig');
            const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
            const isDev = window.location.hostname === "localhost";
            await addDoc(collection(db, isDev ? "installClicksDev" : "installClicks"), {
                timestamp: serverTimestamp(),
                language: window.location.pathname.includes('/en/') ? 'en' : 'ar',
                platform: /android/i.test(navigator.userAgent) ? 'Android' :
                    /iPad|iPhone|iPod/.test(navigator.userAgent) ? 'iOS' : 'Other',
                userAgent: navigator.userAgent || '',
                source: 'bottom-menu'
            });
        } catch (error) {
            console.error("Error logging install click:", error);
        }
    };

    const getButtonStyle = (buttonName) => ({
        backgroundColor: activeButton === buttonName ? '#e8f5e9' : 'transparent',
        color: activeButton === buttonName ? '#198754' : '#000000',
        border: 'none',
        padding: '0',
        cursor: 'pointer',
        fontFamily: 'Arial, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: '50px',
        transition: 'all 0.3s ease',
        borderRadius: '8px',
        paddingTop: '4px'
    });

    return (
        <>
            {/* Social Media Popup - Hidden if menu is collapsed */}
            {isShareOpen && !isCollapsed && (
                <div style={{
                    position: 'fixed',
                    bottom: '80px',
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    zIndex: 1001,
                    animation: 'fadeIn 0.2s ease-out'
                }}>
                    <style>
                        {`
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(14px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}
                    </style>
                    <FacebookShareButton url={shareUrl} aria-label="Facebook" beforeOnClick={() => handleShareClick('facebook')}>
                        <FacebookIcon size={size} round />
                    </FacebookShareButton>
                    <FacebookMessengerShareButton url={shareUrl} aria-label="Messenger" beforeOnClick={() => handleShareClick('messenger')}>
                        <FacebookMessengerIcon size={size} round />
                    </FacebookMessengerShareButton>
                    <WhatsappShareButton url={shareUrl} aria-label="WhatsApp" beforeOnClick={() => handleShareClick('whatsapp')}>
                        <WhatsappIcon size={size} round />
                    </WhatsappShareButton>
                    <TelegramShareButton url={shareUrl} aria-label="Telegram" beforeOnClick={() => handleShareClick('telegram')}>
                        <TelegramIcon size={size} round />
                    </TelegramShareButton>
                    <TwitterShareButton url={shareUrl} aria-label="Twitter" beforeOnClick={() => handleShareClick('twitter')}>
                        <TwitterIcon size={size} round />
                    </TwitterShareButton>
                </div>
            )}

            {/* Overlay to close share */}
            {isShareOpen && (
                <div
                    onClick={() => {
                        setIsShareOpen(false);
                        setActiveButton(null);
                    }}
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                />
            )}

            {/* Toggle Arrow Button (Fixed to the side) */}
            <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                style={{
                    position: 'fixed',
                    bottom: isCollapsed ? '12px' : (isInstalled ? '70px' : '60px'), // Moves down when hidden
                    ...toggleSideStyle,
                    zIndex: 1001,
                    backgroundColor: '#198754', // Brand green
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50%',
                    width: '35px',
                    height: '35px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer'
                }}
            >
                <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                        transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease'
                    }}
                >
                    <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
            </button>

            {/* Bottom Navigation Menu */}
            <div style={{
                position: 'fixed',
                bottom: isCollapsed ? '-80px' : (isInstalled ? '15px' : '0'),

                left: '10px',
                right: '10px',
                backgroundColor: '#ffffff',
                height: '60px',
                zIndex: 1000,
                display: 'flex',
                flexDirection: isEnglish ? 'row' : 'row-reverse',
                justifyContent: 'space-around',
                alignItems: 'center',
                boxShadow: '0 -1px 12px rgba(0,0,0,0.15)',
                border: '1px solid #e0e0e0',
                borderRadius: '15px',
                transition: 'bottom 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                visibility: isCollapsed ? 'hidden' : 'visible'
            }}>
                <button onClick={handleShareToggle} style={getButtonStyle('share')} aria-label={isEnglish ? "Share" : "مشاركة"}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="18" cy="5" r="3"></circle>
                        <circle cx="6" cy="12" r="3"></circle>
                        <circle cx="18" cy="19" r="3"></circle>
                        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                        <b>{isEnglish ? 'Share' : 'شارك'}</b>
                    </span>
                </button>

                <button onClick={handleHomeClick} style={getButtonStyle('home')} aria-label={isEnglish ? "Home" : "الرئيسية"}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                    </svg>
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                        <b> {isEnglish ? 'Home' : 'الرئيسية'}</b>
                    </span>
                </button>

                <button onClick={handleInstallClick} style={getButtonStyle('install')} aria-label={isEnglish ? "Install App" : "ثبت التطبيق"}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                        <b>{isEnglish ? 'Install' : 'تثبيت'}</b>
                    </span>
                </button>

                <button onClick={handleFeedbackClick} style={getButtonStyle('feedback')} aria-label={isEnglish ? "Feedback" : "خبرنا رأيك"}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                        <b>{isEnglish ? 'Feedback' : 'رأيك'}</b>
                    </span>
                </button>
            </div>
        </>
    );
};

export default BottomMenu;